import { supabase } from "../supabaseClient";
import { withTimeout } from "./asyncTimeout";
import { traceAsync } from "./perfTrace";

const AUTH_SESSION_TIMEOUT_MS = 12000;
const AUTH_ROLE_TIMEOUT_MS = 12000;
const ACCESS_CACHE_KEY = "strobe.userAccess";

function getPersistedSupabaseUserId() {
  try {
    for (const key of Object.keys(localStorage)) {
      if (!key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;
      const value = localStorage.getItem(key);
      if (!value) continue;

      const parsed = JSON.parse(value);
      const userId = parsed?.user?.id;
      if (userId) return userId;
    }
  } catch {
    return null;
  }

  return null;
}

export function hasPersistedSupabaseSession() {
  return Boolean(getPersistedSupabaseUserId());
}

function readCachedAccess(userId) {
  try {
    const cached = JSON.parse(
      localStorage.getItem(ACCESS_CACHE_KEY) ||
        sessionStorage.getItem(ACCESS_CACHE_KEY) ||
        "null"
    );
    return cached?.userId === userId ? cached.access : null;
  } catch {
    return null;
  }
}

export function getCachedUserAccessSnapshot() {
  try {
    const persistedUserId = getPersistedSupabaseUserId();
    const cached = JSON.parse(
      localStorage.getItem(ACCESS_CACHE_KEY) ||
        sessionStorage.getItem(ACCESS_CACHE_KEY) ||
        "null"
    );
    if (!persistedUserId || cached?.userId !== persistedUserId || !cached?.access) return null;

    return {
      userId: cached.userId,
      ...cached.access,
      fromCache: true,
    };
  } catch {
    return null;
  }
}

function cacheAccess(userId, access) {
  if (!userId) return;
  localStorage.setItem(
    ACCESS_CACHE_KEY,
    JSON.stringify({
      userId,
      access: {
        isAdmin: access.isAdmin,
        adminRole: access.adminRole,
        isBroker: access.isBroker,
        brokerRole: access.brokerRole,
      },
    })
  );
}

export function clearCachedUserAccess() {
  try {
    localStorage.removeItem(ACCESS_CACHE_KEY);
    sessionStorage.removeItem(ACCESS_CACHE_KEY);
  } catch {
    // Ignore storage failures; logout should still continue.
  }
}

async function getRestoredSession() {
  const {
    data: { session },
    error: sessionError,
  } = await traceAsync(
    "auth.getSession",
    () => withTimeout(
      supabase.auth.getSession(),
      AUTH_SESSION_TIMEOUT_MS,
      "Auth session restore timed out"
    ),
    { hasPersistedSession: hasPersistedSupabaseSession() }
  );

  if (session || sessionError || !hasPersistedSupabaseSession()) {
    return { session, sessionError, pending: false };
  }

  const {
    data: { session: refreshedSession },
    error: refreshError,
  } = await traceAsync(
    "auth.refreshSession",
    () => withTimeout(
      supabase.auth.refreshSession(),
      AUTH_SESSION_TIMEOUT_MS,
      "Auth session refresh timed out"
    ),
    {}
  );

  if (refreshedSession || refreshError) {
    return { session: refreshedSession, sessionError: refreshError, pending: false };
  }

  return { session: null, sessionError: null, pending: true };
}

async function resolveUserAccess() {
  const { session, sessionError, pending } = await getRestoredSession();

  const user = session?.user;

  if (pending) {
    return {
      user: null,
      isAdmin: false,
      adminRole: null,
      isBroker: false,
      brokerRole: null,
      accessPending: true,
    };
  }

  if (sessionError || !user) {
    return { user: null, isAdmin: false, isBroker: false };
  }

  try {
    const [
      { data: adminRows, error: adminError },
      { data: brokerRows, error: brokerError },
    ] = await Promise.all([
      traceAsync(
        "auth.role.admin",
        () => withTimeout(
          supabase.from("admin_users").select("role").eq("user_id", user.id).limit(1),
          AUTH_ROLE_TIMEOUT_MS,
          "Admin role check timed out"
        ),
        {}
      ),
      traceAsync(
        "auth.role.broker",
        () => withTimeout(
          supabase.from("broker_account_members").select("role").eq("user_id", user.id).limit(1),
          AUTH_ROLE_TIMEOUT_MS,
          "Broker role check timed out"
        ),
        {}
      ),
    ]);

    if (adminError || brokerError) {
      throw adminError || brokerError;
    }

    const access = {
      user,
      isAdmin: (adminRows || []).length > 0,
      adminRole: adminRows?.[0]?.role || null,
      isBroker: (brokerRows || []).length > 0,
      brokerRole: brokerRows?.[0]?.role || null,
    };

    cacheAccess(user.id, access);
    return access;
  } catch (error) {
    console.warn("Auth role check failed:", error?.message || error);
    const cached = readCachedAccess(user.id);

    if (cached) {
      return {
        user,
        ...cached,
        fromCache: true,
      };
    }

    return {
      user,
      isAdmin: false,
      adminRole: null,
      isBroker: false,
      brokerRole: null,
      accessPending: true,
    };
  }
}

export async function getUserAccess() {
  try {
    return await resolveUserAccess();
  } catch (error) {
    console.warn("Auth access check failed:", error?.message || error);
    if (hasPersistedSupabaseSession()) {
      return {
        user: null,
        isAdmin: false,
        adminRole: null,
        isBroker: false,
        brokerRole: null,
        accessPending: true,
      };
    }
    return { user: null, isAdmin: false, isBroker: false };
  }
}

export function defaultRouteForAccess(access) {
  if (access.isAdmin && access.isBroker) return "/choose-portal";
  if (access.isAdmin) return "/admin";
  return "/portal";
}
